import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { firestore } from "./FirebaseService";
import { calculateWorkoutsPerWeek } from "../utils/DiactiveUtils";
import { getArrowString } from "../utils/TrendArrowUtils";

const workoutsCollection = collection(firestore, "workouts");

export const fetchWorkouts = async (userId) => {
  const q = query(
    workoutsCollection,
    where("userId", "==", userId),
    orderBy("createdAt", "asc")
  );
  const querySnapshot = await getDocs(q);

  const workouts = querySnapshot.docs.map((doc) => {
    const workout = doc.data();
    return {
      id: doc.id,
      ...workout
    };
  });
  return workouts;
};

// Expects workouts ordered oldest first, as returned by fetchWorkouts
export const calculateWorkoutStats = (workouts) => {
  if (workouts.length === 0) {
    return {
      workoutCount: 0,
      workoutsPerWeek: 0,
      daysSinceLastWorkout: null,
      heartRateMin: null,
      heartRateMax: null,
      heartRateAvg: null,
      glycemiaPreAvg: null,
      glycemiaPostAvg: null,
      trendArrowPreMode: null,
      trendArrowPostMode: null
    };
  }

  const workoutCount = workouts.length;
  
  // Days since last workout (most recent workout is now last in asc order)
  const mostRecentWorkout = workouts[workouts.length - 1];
  const daysSinceLastWorkout = mostRecentWorkout?.createdAt 
    ? Math.floor((new Date() - mostRecentWorkout.createdAt.toDate()) / (1000 * 60 * 60 * 24))
    : null;

  const workoutsPerWeek = calculateWorkoutsPerWeek(workouts);

  // Filter workouts that have heart rate data
  const workoutsWithHeartRate = workouts.filter(w => 
    w.heartRateMin != null && w.heartRateMax != null && w.heartRateAvg != null
  );

  // Calculate heart rate statistics
  let heartRateMin = null;
  let heartRateMax = null;
  let heartRateAvg = null;

  if (workoutsWithHeartRate.length > 0) {
    // Minimum of all minimum heart rates
    heartRateMin = Math.min(...workoutsWithHeartRate.map(w => w.heartRateMin));
    
    // Maximum of all maximum heart rates
    heartRateMax = Math.max(...workoutsWithHeartRate.map(w => w.heartRateMax));
    
    // Average of all average heart rates
    const totalHeartRateAvg = workoutsWithHeartRate.reduce((sum, w) => sum + w.heartRateAvg, 0);
    heartRateAvg = Math.round(totalHeartRateAvg / workoutsWithHeartRate.length);
  }

  // Filter workouts that have glycemia data
  const workoutsWithGlycemiaPre = workouts.filter(w => w.glycemiaPre != null);
  const workoutsWithGlycemiaPost = workouts.filter(w => w.glycemiaPost != null);

  // Calculate glycemia averages
  let glycemiaPreAvg = null;
  let glycemiaPostAvg = null;

  if (workoutsWithGlycemiaPre.length > 0) {
    const totalGlycemiaPre = workoutsWithGlycemiaPre.reduce((sum, w) => sum + w.glycemiaPre, 0);
    glycemiaPreAvg = Math.round(totalGlycemiaPre / workoutsWithGlycemiaPre.length);
  }

  if (workoutsWithGlycemiaPost.length > 0) {
    const totalGlycemiaPost = workoutsWithGlycemiaPost.reduce((sum, w) => sum + w.glycemiaPost, 0);
    glycemiaPostAvg = Math.round(totalGlycemiaPost / workoutsWithGlycemiaPost.length);
  }

  // Most frequent arrow: the most frequent stored value, shown using the most frequent arrow type
  const trendArrowTypeMode = getMostFrequentValue(workouts.map(w => w.trendArrowType));
  const trendArrowPreMode = getArrowString(trendArrowTypeMode, getMostFrequentValue(workouts.map(w => w.trendArrowPre))) ?? null;
  const trendArrowPostMode = getArrowString(trendArrowTypeMode, getMostFrequentValue(workouts.map(w => w.trendArrowPost))) ?? null;

  return {
    workoutCount,
    daysSinceLastWorkout,
    workoutsPerWeek,
    heartRateMin,
    heartRateMax,
    heartRateAvg,
    glycemiaPreAvg,
    glycemiaPostAvg,
    trendArrowPreMode,
    trendArrowPostMode
  };
};

// Returns the most frequent value, ignoring null/undefined. Ties go to the lowest value.
const getMostFrequentValue = (values) => {
  const counts = new Map();
  for (const value of values) {
    if (value == null) continue;
    counts.set(value, (counts.get(value) || 0) + 1);
  }
  let mostFrequent = null;
  let maxCount = 0;
  for (const [value, count] of counts) {
    if (count > maxCount || (count === maxCount && value < mostFrequent)) {
      mostFrequent = value;
      maxCount = count;
    }
  }
  return mostFrequent;
};